import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Snackbar,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import {
  getFindJobQueryOptions,
  getEditJobMutationOptions,
} from "../../../../../api/bpm/endpoints";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/roles/$jobId/edit")({
  component: RoleEditPage,
  staticData: {
    title: "routes.editRole",
  },
});

const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
});

type FormValues = z.infer<typeof schema>;

function RoleEditPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { jobId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  const {
    data,
    isLoading: isLoadingRole,
    error: errorRole,
  } = useQuery({ ...getFindJobQueryOptions(tenant, Number(jobId)) });

  const role: any = data;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (role) {
      reset({
        name: role.name || "",
        description: role.description || "",
      });
    }
  }, [role, reset]);

  const mutation = useMutation({
    ...getEditJobMutationOptions(),

    onSuccess: () => {
      setSnackbar({
        open: true,
        message: t("admin.roles.updateSuccess"),
        severity: "success",
      });
      queryClient.invalidateQueries({
        queryKey: [
          'findJob',
          { jobId: Number(jobId), tenant },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['listJob'],
      });
      navigate({ to: `/admin/roles/${jobId}` });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: t("admin.roles.updateError"),
        severity: "error",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate({
      tenant, jobId: Number(jobId),
      data: { name: data.name, description: data.description },
    });
  };

  if (isLoadingRole) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (errorRole) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erro ao carregar dados do cargo.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: `/admin/roles`, search: { page: 1, size: 10 } })
          }
        >{t("common.back")}</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>{t("admin.roles.editTitle")} {role?.name}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Dados Básicos
        </Typography>

        <Stack spacing={2.5} sx={{ mb: 4 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do Cargo"
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.common.description")}
                fullWidth
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate({ to: `/admin/roles/${jobId}` })}
            disabled={mutation.isPending}
          >{t("common.cancel").toUpperCase()}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={mutation.isPending}
          >
            SALVAR ALTERAÇÕES
          </Button>
        </Box>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
